import type { CartActionToken } from '@sleed/bizcommerce-api';
import type { BogoOfferTarget, BogoReachPricingMode, DiscountMode, PromotionAction, Tier, TierMode } from '~/lib/schemas/actions';
import { ACTION_TYPE_TO_WIRE, WIRE_TO_ACTION_TYPE } from '~/components/promotion/actions/action-type-meta';

/**
 * Bidirectional mapper between the form action union and the backend
 * `CartActionToken { ActionType, Payload }`. Field names + enums reconciled against
 * Core/server/Libraries/BizWeb.NC.Base/CartActions/* and the legacy admin
 * `domain/promotion-policies/editor/cart-actions/*` (see task-analysis
 * "Action Payload reconciliation").
 *
 * Best-effort / documented ambiguities:
 *  - `combineWithOthers` maps to `AllowCombination` for amount/fixed-price; percentage/bogo
 *    encode combine behaviour in `CombinedDiscountsMethod` instead (no separate bool).
 *  - `applyOnDiscountedProducts` ↔ `DiscountApplicationMethod` (ProductOnly=0/CouponOnly=1/
 *    Maximum=2/Additive=3/Multiplicative=4): none→0, max→2, additive→3, onFinal→4
 *    (Multiplicative = policy applied on the price AFTER the catalog discount). CouponOnly=1
 *    has no form equivalent → reverse-maps to `none`.
 *  - tier family: `IncrementalBy` (IncrementalType None/Quantity/Amount) is only meaningful when
 *    `Increments` is populated; a `fixed` (flat) discount uses the scalar value with empty
 *    Increments, so IncrementalBy=None(0) is the inert default the backend ignores.
 *  - shipping `discountType:'free'` has no backend flag → encoded as 100% Discount.
 */

// ---- enum maps ----------------------------------------------------------------
const INCREMENTAL_BY: Record<TierMode, number> = { fixed: 0, byQuantity: 1, byAmount: 2 };
const INCREMENTAL_BY_REV: Record<number, TierMode> = { 0: 'fixed', 1: 'byQuantity', 2: 'byAmount' };

type ApplyOnDiscounted = Extract<PromotionAction, { type: 'percentage-discount' }>['applyOnDiscountedProducts'];
const COMBINED_METHOD: Record<ApplyOnDiscounted, number> = { none: 0, max: 2, additive: 3, onFinal: 4 };
const COMBINED_METHOD_REV: Record<number, ApplyOnDiscounted> = { 0: 'none', 1: 'none', 2: 'max', 3: 'additive', 4: 'onFinal' };

/** `BizWeb.NC.Base.Enumerations.DiscountMode`: Percentage=0, Amount=1 (absent ⇒ Percentage). */
const DISCOUNT_MODE: Record<DiscountMode, number> = { percentage: 0, amount: 1 };
const discountModeForm = (v: unknown): DiscountMode => (v === 1 || v === 'Amount' ? 'amount' : 'percentage');

/** `BogoReachPricingMode`: AtPriceAfterDiscount=0, AtPriceBeforeDiscount=1 (absent ⇒ 0). */
const REACH_PRICING: Record<BogoReachPricingMode, number> = { afterDiscount: 0, beforeDiscount: 1 };
const reachPricingForm = (v: unknown): BogoReachPricingMode => (v === 1 || v === 'AtPriceBeforeDiscount' ? 'beforeDiscount' : 'afterDiscount');


// ---- helpers ------------------------------------------------------------------
const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const numOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** BOGO offer-target rows (BW-8222). `MaxQuantity` is only sent for product rows; the server ignores it on category rows. */
function offerTargetsToWire(rows: BogoOfferTarget[], withMaxQty: boolean) {
	return rows.map(r => ({
		Id: r.id,
		Discount: r.discount,
		DiscountMode: DISCOUNT_MODE[r.discountMode],
		...(withMaxQty ? { MaxQuantity: r.maxQty } : {})
	}));
}
function offerTargetsToForm(v: unknown, withMaxQty: boolean): BogoOfferTarget[] {
	return arr<Record<string, unknown>>(v).map(r => ({
		id: num(r.Id),
		discount: numOrNull(r.Discount),
		discountMode: discountModeForm(r.DiscountMode),
		maxQty: withMaxQty ? numOrNull(r.MaxQuantity) : null
	}));
}

function tiersToIncrements(tiers: Tier[], valueKey: 'Percentage' | 'Amount' | 'Price') {
	return tiers.map(t => ({ Threshold: t.threshold, [valueKey]: t.value }));
}
function incrementsToTiers(increments: unknown, valueKey: 'Percentage' | 'Amount' | 'Price'): Tier[] {
	const list = arr<Record<string, unknown>>(increments);
	if (list.length === 0) return [{ id: crypto.randomUUID(), threshold: 0, value: 0 }];
	return list.map(i => ({ id: crypto.randomUUID(), threshold: num(i.Threshold), value: num(i[valueKey]) }));
}
/** Tier-family form → payload (IncrementalBy + scalar-when-fixed + Increments-when-tiered). */
function tierPayload(tierMode: TierMode, tiers: Tier[], valueKey: 'Percentage' | 'Amount' | 'Price') {
	const isFixed = tierMode === 'fixed';
	return {
		IncrementalBy: INCREMENTAL_BY[tierMode],
		[valueKey]: isFixed ? (tiers[0]?.value ?? 0) : null,
		Increments: isFixed ? [] : tiersToIncrements(tiers, valueKey)
	};
}
/** Tier-family payload → form { tierMode, tiers }. */
function tierForm(payload: Record<string, unknown>, valueKey: 'Percentage' | 'Amount' | 'Price'): { tierMode: TierMode; tiers: Tier[] } {
	const tierMode = INCREMENTAL_BY_REV[num(payload.IncrementalBy)] ?? 'fixed';
	if (tierMode === 'fixed') return { tierMode, tiers: [{ id: crypto.randomUUID(), threshold: 0, value: num(payload[valueKey]) }] };
	return { tierMode, tiers: incrementsToTiers(payload.Increments, valueKey) };
}

// ---- form → CartActionToken ---------------------------------------------------
export function formActionToToken(action: PromotionAction): CartActionToken {
	const ActionType = ACTION_TYPE_TO_WIRE[action.type];
	let Payload: Record<string, unknown>;

	switch (action.type) {
		case 'percentage-discount':
			Payload = {
				...tierPayload(action.tierMode, action.tiers, 'Percentage'),
				CombinedDiscountsMethod: COMBINED_METHOD[action.applyOnDiscountedProducts]
			};
			break;
		case 'value-discount':
			Payload = {
				...tierPayload(action.tierMode, action.tiers, 'Amount'),
				AllowCombination: action.combineWithOthers,
				ApplyDiscountMode: action.applicationMode === 'perItem' ? 1 : 0
			};
			break;
		case 'fixed-price':
			Payload = { ...tierPayload(action.tierMode, action.tiers, 'Price'), AllowCombination: action.combineWithOthers };
			break;
		case 'bogo':
			Payload = {
				QuantityReach: action.x,
				QuantityOffer: action.y,
				Percentage: action.z,
				MustBeSameProduct: action.sameProductOnly,
				CombinedDiscountsMethod: action.combinedDiscountsMethod ?? 0,
				BogoReachPricingMode: REACH_PRICING[action.reachPricingMode],
				OfferProducts: action.offerProducts.length > 0 ? offerTargetsToWire(action.offerProducts, true) : null,
				OfferCategories: action.offerCategories.length > 0 ? offerTargetsToWire(action.offerCategories, false) : null
			};
			break;
		case 'gift-product':
			Payload = {
				MaxChoices: action.maxSelections,
				Products: action.productIds.map(id => ({ ProductId: id, DimensionId: null, Quantity: 1, MaxQuantity: null })),
				Messages: Object.fromEntries(action.prompts.map(p => [p.locale, { Prompt: p.text }]))
			};
			break;
		case 'per-product-discount':
			Payload = {
				Products: action.rows.map(r => ({
					ProductId: r.productId,
					DimensionId: null,
					Quantity: r.minQty,
					Discount: r.discount,
					DiscountMode: DISCOUNT_MODE[r.discountMode],
					MaxQuantity: r.maxQty
				}))
			};
			break;
		case 'category-discount':
			Payload = {
				MaxChoices: action.maxChoices,
				Categories: action.rows.map(r => ({
					CategoryId: r.categoryId,
					Quantity: r.minQty,
					Discount: r.discount,
					DiscountMode: DISCOUNT_MODE[r.discountMode],
					MaxQuantity: r.maxQty
				})),
				Messages: action.messages
			};
			break;
		case 'shipping-discount':
			Payload = {
				Rules: action.rules.map(r => ({
					ShippingTypeId: r.shippingMethodId,
					Discount: r.discountType === 'percentage' ? r.amount : r.discountType === 'free' ? 100 : null,
					Amount: r.discountType === 'fixed' ? r.amount : null,
					IsAdditional: r.additive,
					StopProcessingOnSuccess: r.stopProcessing
				}))
			};
			break;
	}

	return { ActionType, Payload };
}

// ---- CartActionToken → form ---------------------------------------------------
export function tokenToFormAction(token: CartActionToken): PromotionAction | null {
	const type = WIRE_TO_ACTION_TYPE[token.ActionType ?? ''];
	if (!type) return null;
	const p = (token.Payload ?? {}) as Record<string, unknown>;

	switch (type) {
		case 'percentage-discount':
			return {
				type,
				combineWithOthers: false,
				...tierForm(p, 'Percentage'),
				applyOnDiscountedProducts: COMBINED_METHOD_REV[num(p.CombinedDiscountsMethod)] ?? 'none'
			};
		case 'value-discount':
			return {
				type,
				combineWithOthers: Boolean(p.AllowCombination),
				applicationMode: num(p.ApplyDiscountMode) === 1 ? 'perItem' : 'once',
				...tierForm(p, 'Amount')
			};
		case 'fixed-price':
			return { type, combineWithOthers: Boolean(p.AllowCombination), ...tierForm(p, 'Price') };
		case 'bogo':
			return {
				type,
				combineWithOthers: false,
				x: num(p.QuantityReach, 1),
				y: num(p.QuantityOffer, 1),
				z: numOrNull(p.Percentage),
				sameProductOnly: Boolean(p.MustBeSameProduct),
				...(num(p.CombinedDiscountsMethod) !== 0 ? { combinedDiscountsMethod: num(p.CombinedDiscountsMethod) } : {}),
				reachPricingMode: reachPricingForm(p.BogoReachPricingMode),
				offerProducts: offerTargetsToForm(p.OfferProducts, true),
				offerCategories: offerTargetsToForm(p.OfferCategories, false)
			};
		case 'gift-product': {
			const messages = (p.Messages ?? {}) as Record<string, { Prompt?: string }>;
			const prompts = Object.entries(messages).map(([locale, m]) => ({ locale, text: str(m?.Prompt) }));
			return {
				type,
				combineWithOthers: false,
				maxSelections: num(p.MaxChoices, 1),
				productIds: arr<{ ProductId?: number }>(p.Products).map(i => num(i.ProductId)),
				prompts:
					prompts.length > 0
						? prompts
						: [
								{ locale: 'el', text: '' },
								{ locale: 'en', text: '' }
							]
			};
		}
		case 'per-product-discount':
			return {
				type,
				combineWithOthers: false,
				rows: arr<Record<string, unknown>>(p.Products).map(i => ({
					productId: num(i.ProductId),
					minQty: num(i.Quantity, 1),
					discount: num(i.Discount),
					discountMode: discountModeForm(i.DiscountMode),
					maxQty: numOrNull(i.MaxQuantity)
				}))
			};
		case 'category-discount':
			return {
				type,
				combineWithOthers: false,
				maxChoices: numOrNull(p.MaxChoices),
				rows: arr<Record<string, unknown>>(p.Categories).map(i => ({
					categoryId: num(i.CategoryId),
					minQty: num(i.Quantity, 1),
					discount: num(i.Discount),
					discountMode: discountModeForm(i.DiscountMode),
					maxQty: numOrNull(i.MaxQuantity)
				})),
				messages: p.Messages && typeof p.Messages === 'object' && !Array.isArray(p.Messages) ? (p.Messages as Record<string, unknown>) : {}
			};
		case 'shipping-discount':
			return {
				type,
				combineWithOthers: false,
				rules: arr<Record<string, unknown>>(p.Rules).map(r => ({
					id: crypto.randomUUID(),
					shippingMethodId: r.ShippingTypeId == null ? null : num(r.ShippingTypeId),
					discountType: r.Discount === 100 ? 'free' : r.Amount != null ? 'fixed' : 'percentage',
					amount: num(r.Discount ?? r.Amount),
					additive: Boolean(r.IsAdditional),
					stopProcessing: Boolean(r.StopProcessingOnSuccess)
				}))
			};
	}
}
